import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import { transformSalesData, groupSalesByPeriod } from '../../utils/salesDataUtils';
import { transformPurchasesData } from '../../utils/purchasesDataUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';

const AbcClassificationReport = ({ onBack }) => {
  const { products, sales, purchases, customers, suppliers } = useAppData();
  const { companySettings } = useCompanySettings();
  const [selectedAnalysis, setSelectedAnalysis] = useState('distribution');
  const [periodFilter, setPeriodFilter] = useState('year');
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [abcData, setAbcData] = useState({
    products: [],
    summary: {
      A: { count: 0, value: 0, percentage: 0 },
      B: { count: 0, value: 0, percentage: 0 },
      C: { count: 0, value: 0, percentage: 0 }
    },
    evolution: [],
    recommendations: []
  });
  
  // Analyses disponibles
  const analysisTypes = [
    {
      id: 'distribution',
      name: 'Distribution ABC',
      description: 'Répartition des produits et leur impact financier',
    },
    {
      id: 'products',
      name: 'Produits par catégorie',
      description: 'Liste détaillée des produits dans chaque catégorie',
    },
    {
      id: 'evolution',
      name: 'Évolution de la classification',
      description: 'Changements de la classification au cours du temps',
    },
    {
      id: 'recommendations',
      name: 'Recommandations',
      description: 'Recommandations de gestion par catégorie',
    }
  ];
  
  // Transform sales and purchases data for analysis
  const transformedSalesData = useMemo(() => {
    console.log('🔄 ABC: Transforming sales data for analysis');
    return transformSalesData(sales || [], customers || []);
  }, [sales, customers]);

  const transformedPurchasesData = useMemo(() => {
    console.log('🔄 ABC: Transforming purchases data for analysis');
    return transformPurchasesData(purchases || [], suppliers || []);
  }, [purchases, suppliers]);

  // Calculate product performance metrics from real sales data
  const productPerformanceMetrics = useMemo(() => {
    console.log('📊 ABC: Calculating product performance metrics');
    const metrics = {};
    
    // Initialize metrics for all products
    (products || []).forEach(product => {
      metrics[product.id] = {
        product,
        totalSold: 0,
        totalRevenue: 0,
        totalPurchased: 0,
        totalCost: 0,
        turnoverRate: 0,
        profitMargin: 0,
        lastSaleDate: null,
        lastPurchaseDate: null
      };
    });

    // Add sales data
    transformedSalesData.forEach(sale => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const productId = item.id || item.productId;
          if (metrics[productId]) {
            metrics[productId].totalSold += Number(item.quantity || 0);
            metrics[productId].totalRevenue += Number(item.total || (item.quantity * item.price) || 0);
            const saleDate = new Date(sale.rawDate);
            if (!metrics[productId].lastSaleDate || saleDate > metrics[productId].lastSaleDate) {
              metrics[productId].lastSaleDate = saleDate;
            }
          }
        });
      }
    });

    // Add purchase data
    transformedPurchasesData.forEach(purchase => {
      if (purchase.items && Array.isArray(purchase.items)) {
        purchase.items.forEach(item => {
          const productId = item.id || item.productId;
          if (metrics[productId]) {
            metrics[productId].totalPurchased += Number(item.quantity || 0);
            metrics[productId].totalCost += Number(item.total || (item.quantity * item.price) || 0);
            const purchaseDate = new Date(purchase.rawDate);
            if (!metrics[productId].lastPurchaseDate || purchaseDate > metrics[productId].lastPurchaseDate) {
              metrics[productId].lastPurchaseDate = purchaseDate;
            }
          }
        });
      }
    });

    // Calculate derived metrics
    Object.values(metrics).forEach(metric => {
      // Turnover rate (sales quantity / average stock)
      const avgStock = metric.product.stock || 0;
      metric.turnoverRate = avgStock > 0 ? metric.totalSold / avgStock : 0;
      
      // Profit margin
      metric.profitMargin = metric.totalRevenue > 0 ? 
        ((metric.totalRevenue - metric.totalCost) / metric.totalRevenue) * 100 : 0;
    });

    console.log('✅ ABC: Product performance metrics calculated:', Object.keys(metrics).length);
    return metrics;
  }, [products, transformedSalesData, transformedPurchasesData]);

  const calculateAbcClassification = useCallback(() => {
    setIsCalculating(true);
    
    setTimeout(() => {
      // Utiliser uniquement les produits réels
      if (!products || products.length === 0) {
        setAbcData({
          products: [],
          summary: {
            A: { count: 0, value: 0, percentage: 0 },
            B: { count: 0, value: 0, percentage: 0 },
            C: { count: 0, value: 0, percentage: 0 }
          },
          evolution: [],
          recommendations: []
        });
        setIsCalculating(false);
        return;
      }
      
      const productsList = products;
      
      // Enhanced sorting: consider both stock value and sales performance
      const sortedProducts = [...productsList].map(product => {
        const stockValue = (product.price || 0) * (product.stock || 0);
        const performanceMetric = productPerformanceMetrics[product.id];
        
        // Weight stock value with sales performance (revenue + turnover rate)
        const salesWeight = performanceMetric ? 
          (performanceMetric.totalRevenue * 0.7) + (performanceMetric.turnoverRate * stockValue * 0.3) : 0;
        
        const combinedValue = stockValue + salesWeight;
        
        return {
          ...product,
          stockValue,
          salesRevenue: performanceMetric?.totalRevenue || 0,
          turnoverRate: performanceMetric?.turnoverRate || 0,
          profitMargin: performanceMetric?.profitMargin || 0,
          combinedValue
        };
      }).sort((a, b) => b.combinedValue - a.combinedValue);
      
      // Calculer la valeur totale
      const totalValue = sortedProducts.reduce((sum, product) => sum + product.combinedValue, 0);
      
      // Attribuer une classification ABC
      let cumulativeValue = 0;
      const classifiedProducts = sortedProducts.map((product, index) => {
        cumulativeValue += product.combinedValue;
        const cumulativePercentage = (cumulativeValue / totalValue) * 100;
        
        // Classification ABC standard: A: 0-80%, B: 80-95%, C: 95-100%
        let classification;
        if (cumulativePercentage <= 80) {
          classification = 'A';
        } else if (cumulativePercentage <= 95) {
          classification = 'B';
        } else {
          classification = 'C';
        }
        
        return {
          ...product,
          classification,
          cumulativePercentage: parseFloat(cumulativePercentage.toFixed(2)),
          valuePercentage: parseFloat(((product.combinedValue / totalValue) * 100).toFixed(2))
        };
      });
      
      // Calculer les statistiques par catégorie
      const summary = classifiedProducts.reduce((acc, product) => {
        const category = product.classification;
        acc[category].count++;
        acc[category].value += product.combinedValue;
        return acc;
      }, {
        A: { count: 0, value: 0, percentage: 0 },
        B: { count: 0, value: 0, percentage: 0 },
        C: { count: 0, value: 0, percentage: 0 }
      });
      
      // Calculer les pourcentages
      const totalCount = classifiedProducts.length;
      ['A', 'B', 'C'].forEach(category => {
        summary[category].percentage = parseFloat((summary[category].count / totalCount * 100).toFixed(1));
        summary[category].valuePercentage = parseFloat((summary[category].value / totalValue * 100).toFixed(1));
      });
      
      // Générer l'évolution réelle basée sur les données de ventes historiques
      const evolution = generateRealEvolutionData();
      
      // Générer des recommandations
      const recommendations = [
        {
          category: 'A',
          title: 'Produits A: Contrôle strict et attention maximale',
          actions: [
            'Suivi quotidien des niveaux de stock',
            'Mettre en place un stock de sécurité adapté',
            'Établir des relations privilégiées avec les fournisseurs',
            'Réviser les prix régulièrement'
          ]
        },
        {
          category: 'B',
          title: 'Produits B: Contrôle régulier',
          actions: [
            'Suivi hebdomadaire des niveaux de stock',
            'Réviser les prévisions mensuellement',
            'Optimiser les commandes pour réduire les coûts',
            'Évaluer périodiquement leur potentiel de montée en catégorie A'
          ]
        },
        {
          category: 'C',
          title: 'Produits C: Gestion simplifiée',
          actions: [
            'Automatiser les commandes avec des quantités fixes',
            'Réduire la fréquence des contrôles d\'inventaire',
            'Évaluer la possibilité de réduire la gamme',
            'Considérer des commandes groupées pour réduire les coûts administratifs'
          ]
        }
      ];
      
      // Mettre à jour l'état
      setAbcData({
        products: classifiedProducts,
        summary,
        evolution,
        recommendations
      });
      
      setIsCalculating(false);
    }, 1000);
  }, [products, productPerformanceMetrics]);

  // Calcul des données ABC
  useEffect(() => {
    calculateAbcClassification();
  }, [periodFilter, calculateAbcClassification]);
  
  // Générer des données d'évolution réelles basées sur les ventes historiques
  const generateRealEvolutionData = useCallback(() => {
    console.log('📈 ABC: Generating real evolution data from sales history');
    
    if (!transformedSalesData || transformedSalesData.length === 0) {
      console.log('⚠️ ABC: No sales data available for evolution analysis');
      return [];
    }

    // Group sales by month for the last 12 months
    const salesByMonth = groupSalesByPeriod(transformedSalesData, 'month');
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const evolution = [];
    
    // Get last 12 months of data
    const currentDate = new Date();
    for (let i = 11; i >= 0; i--) {
      const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      const monthName = months[targetDate.getMonth()];
      
      const monthSales = salesByMonth.find(s => s.period === monthKey);
      
      if (monthSales && monthSales.sales.length > 0) {
        // Calculate ABC classification for this month's sales
        const monthlyProductMetrics = {};
        
        monthSales.sales.forEach(sale => {
          if (sale.items && Array.isArray(sale.items)) {
            sale.items.forEach(item => {
              const productId = item.id || item.productId;
              const product = products?.find(p => p.id === productId);
              if (product) {
                if (!monthlyProductMetrics[productId]) {
                  monthlyProductMetrics[productId] = {
                    product,
                    revenue: 0
                  };
                }
                monthlyProductMetrics[productId].revenue += Number(item.total || (item.quantity * item.price) || 0);
              }
            });
          }
        });
        
        // Sort products by revenue and classify
        const sortedProducts = Object.values(monthlyProductMetrics)
          .sort((a, b) => b.revenue - a.revenue);
        
        const totalRevenue = sortedProducts.reduce((sum, p) => sum + p.revenue, 0);
        let cumulativeRevenue = 0;
        let classACount = 0, classBCount = 0, classCCount = 0;
        
        sortedProducts.forEach(productMetric => {
          cumulativeRevenue += productMetric.revenue;
          const cumulativePercentage = (cumulativeRevenue / totalRevenue) * 100;
          
          if (cumulativePercentage <= 80) {
            classACount++;
          } else if (cumulativePercentage <= 95) {
            classBCount++;
          } else {
            classCCount++;
          }
        });
        
        const totalProducts = sortedProducts.length;
        evolution.push({
          month: monthName,
          A: totalProducts > 0 ? parseFloat(((classACount / totalProducts) * 100).toFixed(1)) : 0,
          B: totalProducts > 0 ? parseFloat(((classBCount / totalProducts) * 100).toFixed(1)) : 0,
          C: totalProducts > 0 ? parseFloat(((classCCount / totalProducts) * 100).toFixed(1)) : 0
        });
      } else {
        // No sales data for this month
      evolution.push({
          month: monthName,
          A: 0,
          B: 0,
          C: 0
        });
      }
    }
    
    console.log('✅ ABC: Real evolution data generated:', evolution.length, 'months');
    return evolution;
  }, [transformedSalesData, products]);
  
  // Fonction pour générer le rapport PDF
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('📄 ABC: Generating PDF report...');
      console.log('📄 ABC: Company settings:', companySettings);
      console.log('📄 ABC: ABC data:', abcData);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const primaryRed = [220, 38, 127];
      
      // Add header with company logo and name
      const addHeaderFooter = async () => {
        let headerYPosition = 10;
        
        if (companySettings?.logo) {
          try {
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            headerYPosition = 18;
          } catch (error) {
            console.error('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0);
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 15);
            headerYPosition = 18;
          }
        } else {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0);
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 15);
          headerYPosition = 18;
        }
        
        // Header line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number (EXACTLY like Sales)
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`1`, pageWidth - 25, pageHeight - 8);
        
        return headerYPosition + 5;
      };
      
      let yPosition = await addHeaderFooter();
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT DE CLASSIFICATION ABC', 20, yPosition + 10);
      
      yPosition += 25;
      
      // Summary section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('RÉSUMÉ DE LA CLASSIFICATION', 20, yPosition);
      
      yPosition += 10;
      
      // Summary table
      const summaryData = [
        ['Classe A', `${abcData.summary?.A?.count || 0} produits`, `${abcData.summary?.A?.percentage || 0}%`, formatCurrency(abcData.summary?.A?.value || 0)],
        ['Classe B', `${abcData.summary?.B?.count || 0} produits`, `${abcData.summary?.B?.percentage || 0}%`, formatCurrency(abcData.summary?.B?.value || 0)],
        ['Classe C', `${abcData.summary?.C?.count || 0} produits`, `${abcData.summary?.C?.percentage || 0}%`, formatCurrency(abcData.summary?.C?.value || 0)]
      ];
      
      console.log('📄 ABC: Summary data for PDF:', summaryData);
      
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Catégorie', 'Nombre', 'Pourcentage', 'Valeur Totale']],
          body: summaryData,
          theme: 'plain',
          styles: {
            fontSize: 9,
            cellPadding: 3,
            textColor: [0, 0, 0]
          },
          headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          margin: { left: 20, right: 20 }
        });
        console.log('✅ ABC: Summary table added successfully');
      } catch (tableError) {
        console.error('❌ ABC: Error adding summary table:', tableError);
        throw tableError;
      }
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Products by category
      if (yPosition > pageHeight - 50) {
        pdf.addPage();
        yPosition = await addHeaderFooter();
      }
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('PRODUITS PAR CATÉGORIE', 20, yPosition);
      
      yPosition += 10;
      
      // Top 20 products table
      const topProducts = (abcData.products || []).slice(0, 20);
      const productsData = topProducts.map(product => [
        product.name || 'Produit sans nom',
        product.classification || 'N/A',
        formatCurrency(product.combinedValue || product.stockValue || 0),
        `${product.valuePercentage || 0}%`,
        `${product.stock || 0}`
      ]);
      
      console.log('📄 ABC: Products data for PDF:', productsData.length, 'products');
      
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Produit', 'Classe', 'Valeur Combinée', '% Valeur', 'Stock']],
          body: productsData,
          theme: 'plain',
          styles: {
            fontSize: 8,
            cellPadding: 2,
            textColor: [0, 0, 0]
          },
          headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          margin: { left: 20, right: 20 }
        });
        console.log('✅ ABC: Products table added successfully');
      } catch (tableError) {
        console.error('❌ ABC: Error adding products table:', tableError);
        throw tableError;
      }
      
      // Save the PDF
      console.log('📄 ABC: About to save PDF...');
      pdf.save('classification-abc-rapport.pdf');
      console.log('✅ ABC: PDF report generated successfully');
      
    } catch (error) {
      console.error('❌ ABC: Error generating PDF:', error);
      alert('Erreur lors de la génération du rapport PDF');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Couleurs pour les graphiques
  const COLORS = ['#10B981', '#3B82F6', '#F59E0B'];
  
  // Formatage monétaire avec virgules
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '0 DA';
    const number = Math.round(Number(value));
    let str = number.toString();
    let result = '';
    let count = 0;
    for (let i = str.length - 1; i >= 0; i--) {
      if (count === 3) {
        result = ',' + result;
        count = 0;
      }
      result = str[i] + result;
      count++;
    }
    return `${result} DA`;
  };
  
  // Rendu du contenu en fonction de l'analyse sélectionnée
  const renderAnalysisContent = () => {
    switch (selectedAnalysis) {
      case 'distribution':
        return (
          <>
            {/* Cards pour les stats ABC */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {['A', 'B', 'C'].map((category, index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Classe {category}</h3>
                    <div 
                      className={`w-4 h-4 rounded-full`}
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/70">Produits:</span>
                      <span className="text-white font-semibold">{abcData.summary[category].count}</span>
                  </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Pourcentage:</span>
                      <span className="text-white font-semibold">{abcData.summary[category].percentage}%</span>
                      </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Valeur:</span>
                      <span className="text-white font-semibold">{formatCurrency(abcData.summary[category].value)}</span>
                      </div>
                    </div>
                </motion.div>
              ))}
            </div>
            
            {/* Graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Graphique en secteurs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Répartition par nombre de produits</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                        { name: 'Classe A', value: abcData.summary.A.count, color: COLORS[0] },
                        { name: 'Classe B', value: abcData.summary.B.count, color: COLORS[1] },
                        { name: 'Classe C', value: abcData.summary.C.count, color: COLORS[2] }
                        ]}
                        cx="50%"
                        cy="50%"
                      outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                      {[
                        { name: 'Classe A', value: abcData.summary.A.count, color: COLORS[0] },
                        { name: 'Classe B', value: abcData.summary.B.count, color: COLORS[1] },
                        { name: 'Classe C', value: abcData.summary.C.count, color: COLORS[2] }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                    </PieChart>
                  </ResponsiveContainer>
              </div>
              
              {/* Graphique en barres */}
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Répartition par valeur</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                        data={[
                      { name: 'Classe A', value: abcData.summary.A.value, percentage: abcData.summary.A.valuePercentage },
                      { name: 'Classe B', value: abcData.summary.B.value, percentage: abcData.summary.B.valuePercentage },
                      { name: 'Classe C', value: abcData.summary.C.value, percentage: abcData.summary.C.valuePercentage }
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="white" />
                    <YAxis stroke="white" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }}
                      formatter={(value, name) => [formatCurrency(value), 'Valeur']}
                    />
                    <Bar dataKey="value" fill={COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        );
      
      case 'products':
        return (
          <div className="space-y-6">
            {['A', 'B', 'C'].map((category, index) => (
              <div key={category} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-4">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <h3 className="text-xl font-bold text-white">Classe {category}</h3>
                  <span className="text-white/60">({abcData.summary[category].count} produits)</span>
            </div>
            
            <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left text-white/80 py-2">Produit</th>
                        <th className="text-left text-white/80 py-2">Stock</th>
                        <th className="text-left text-white/80 py-2">Prix</th>
                        <th className="text-left text-white/80 py-2">Valeur</th>
                        <th className="text-left text-white/80 py-2">% Valeur</th>
                        <th className="text-left text-white/80 py-2">Rotation</th>
                  </tr>
                </thead>
                    <tbody>
                      {abcData.products
                        .filter(product => product.classification === category)
                        .slice(0, 10)
                        .map((product, idx) => (
                          <tr key={idx} className="border-b border-white/10">
                            <td className="text-white py-2">{product.name}</td>
                            <td className="text-white/80 py-2">{product.stock || 0}</td>
                            <td className="text-white/80 py-2">{formatCurrency(product.price)}</td>
                            <td className="text-white/80 py-2">{formatCurrency(product.combinedValue || product.stockValue)}</td>
                            <td className="text-white/80 py-2">{product.valuePercentage}%</td>
                            <td className="text-white/80 py-2">{product.turnoverRate?.toFixed(2) || '0.00'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </div>
            ))}
          </div>
        );
      
      case 'evolution':
        return (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4">Évolution de la classification ABC (12 derniers mois)</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={abcData.evolution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="white" />
                <YAxis stroke="white" />
                    <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: 'white'
                  }} 
                />
                <Legend />
                <Line type="monotone" dataKey="A" stroke={COLORS[0]} strokeWidth={3} name="Classe A" />
                <Line type="monotone" dataKey="B" stroke={COLORS[1]} strokeWidth={3} name="Classe B" />
                <Line type="monotone" dataKey="C" stroke={COLORS[2]} strokeWidth={3} name="Classe C" />
              </LineChart>
                </ResponsiveContainer>
            
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="text-white/80 text-sm">
                <strong>Note:</strong> Cette évolution est basée sur les données de ventes réelles des 12 derniers mois. 
                Les mois sans données de ventes apparaissent avec des valeurs nulles.
                  </p>
                </div>
              </div>
        );
      
      case 'recommendations':
        return (
          <div className="space-y-8">
            {abcData.recommendations.map((rec) => (
              <div key={rec.category} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-4">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: COLORS[rec.category === 'A' ? 0 : rec.category === 'B' ? 1 : 2] }}
                  >
                    {rec.category}
                  </div>
                  <h3 className="text-xl font-bold text-white">{rec.title}</h3>
                </div>
                
                <ul className="space-y-2 ml-9">
                  {rec.actions.map((action, idx) => (
                    <li key={idx} className="text-white/80 flex items-start space-x-2">
                      <span className="text-green-400 mt-1">•</span>
                      <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                
                {rec.category === 'A' && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl ml-9">
                    <p className="text-white/80 text-sm">
                      <strong>Stratégie recommandée :</strong> Ces {abcData.summary.A.count} produits représentent 
                      {' '}{abcData.summary.A.valuePercentage}% de la valeur totale. Ils méritent une attention particulière 
                      et des investissements en gestion pour maximiser leur performance.
                    </p>
                  </div>
                )}
                
                {rec.category === 'B' && (
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl ml-9">
                    <p className="text-white/80 text-sm">
                      <strong>Stratégie recommandée :</strong> Ces {abcData.summary.B.count} produits représentent 
                      {' '}{abcData.summary.B.valuePercentage}% de la valeur totale. Ils nécessitent un suivi régulier 
                      et peuvent potentiellement être optimisés pour passer en catégorie A.
                    </p>
                  </div>
                )}
                
                {rec.category === 'C' && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl ml-9">
                    <p className="text-white/80 text-sm">
                      <strong>Stratégie recommandée :</strong> Ces {abcData.summary.C.count} produits ne représentent que 
                      {' '}{abcData.summary.C.valuePercentage}% de la valeur totale. Privilégiez une gestion automatisée 
                      et simplifiée pour minimiser les coûts administratifs.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      
      default:
        return <div className="text-white">Sélectionnez une analyse</div>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with back button and PDF export */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Classification ABC</h1>
          <p className="text-white/60 text-sm mt-1">
              Analysez la distribution et l'importance de vos produits avec données temps réel
          </p>
        </div>
        </div>
        
        {/* Export PDF Button - Top Right */}
        <motion.button
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Génération...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Exporter PDF Complet</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Controls */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 flex flex-wrap gap-4 justify-between">
        <div className="flex items-center space-x-6">
          {/* Type d'analyse */}
          <div className="relative">
            <select
              value={selectedAnalysis}
              onChange={(e) => setSelectedAnalysis(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
            >
              {analysisTypes.map((type) => (
                <option key={type.id} value={type.id} className="bg-gray-800 text-white">
                  {type.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/60 pointer-events-none" />
          </div>
          

        </div>
        
        {/* Force Refresh Button */}
        <motion.button
          onClick={() => {
            console.log('🔄 ABC: Force refresh triggered');
            calculateAbcClassification();
          }}
            disabled={isCalculating}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
        >
          {isCalculating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Actualisation...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Actualiser</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Contenu principal */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6">
        {isCalculating ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white/80">Calcul de la classification ABC en cours...</p>
              <p className="text-white/60 text-sm mt-2">Analyse des données de ventes et d'inventaire</p>
        </div>
      </div>
        ) : (
          renderAnalysisContent()
        )}
      </div>
    </motion.div>
  );
};

export default AbcClassificationReport; 